import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, FolderOpen, UserPlus, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function InvitePage({ user }) {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const [linkInfo, setLinkInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState('');
  const claimAttempted = useRef(false);

  const isLoggedIn = !!localStorage.getItem('noyo_token');

  // Fetch link info on mount
  useEffect(() => {
    const fetchLinkInfo = async () => {
      try {
        const res = await axios.get(`${API}/share-link/${linkId}`);
        setLinkInfo(res.data);
      } catch (err) {
        setError('This share link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchLinkInfo();
  }, [linkId]);

  // Auto-claim when user is logged in and link is valid
  useEffect(() => {
    if (isLoggedIn && linkInfo && !error && !claimAttempted.current) {
      claimAttempted.current = true;
      doClaim();
    }
  }, [isLoggedIn, linkInfo]);

  const doClaim = async () => {
    setClaiming(true);
    try {
      const res = await axios.post(`${API}/share-link/${linkId}/claim`);
      const folderId = res.data.folder_id;
      // Go straight to the shared folder
      navigate(`/stories/shared/${folderId}`, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to join folder';
      if (msg === 'Cannot claim your own share link') {
        // Owner opened their own link — just go to the folder
        // We need the folder_id from the link info
        if (linkInfo?.folder_id) {
          navigate(`/stories/${linkInfo.folder_id}`, { replace: true });
        } else {
          navigate('/stories', { replace: true });
        }
      } else {
        setError(msg);
        setClaiming(false);
      }
    }
  };

  const handleJoinClick = async () => {
    if (isLoggedIn) {
      doClaim();
    } else {
      // Auto-create guest account and join immediately
      setClaiming(true);
      try {
        const guestRes = await axios.post(`${API}/auth/guest`, { name: 'Guest' });
        if (guestRes.data?.token) {
          localStorage.setItem('noyo_token', guestRes.data.token);
          // Set auth header for subsequent requests
          axios.defaults.headers.common['Authorization'] = `Bearer ${guestRes.data.token}`;
          // Now claim the share link as the guest
          const claimRes = await axios.post(`${API}/share-link/${linkId}/claim`);
          const folderId = claimRes.data.folder_id;
          // Force full page reload so App.js picks up the new auth state
          window.location.href = `/stories/shared/${folderId}`;
        }
      } catch (err) {
        console.error('Guest join failed:', err);
        // Fallback to signup flow
        localStorage.setItem('pending_invite_link', linkId);
        navigate('/signup');
      }
    }
  };

  if (loading || (isLoggedIn && linkInfo && !error)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
          {user && <p className="text-sm text-muted-foreground">Joining folder...</p>}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border rounded-2xl p-8 max-w-md w-full text-center"
        >
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-serif font-semibold text-foreground mb-2">
            {error.includes('expired') || error.includes('invalid') ? 'Link Not Found' : 'Something went wrong'}
          </h1>
          <p className="text-sm text-muted-foreground mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
            data-testid="invite-error-home"
          >
            Go to NOYO
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full text-center shadow-xl"
        data-testid="invite-card"
      >
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <FolderOpen className="w-7 h-7 text-primary" />
        </div>
        
        <h1 className="text-xl font-serif font-semibold text-foreground mb-1">
          You're Invited!
        </h1>
        <p className="text-sm text-muted-foreground mb-1">
          <span className="font-medium text-foreground">{linkInfo.owner_name}</span> invited you to collaborate
        </p>
        
        <div className="bg-secondary/50 rounded-xl p-3 my-4">
          <span className="font-serif font-medium text-foreground">
            {linkInfo.folder_name}
          </span>
        </div>
        
        <p className="text-xs text-muted-foreground mb-5">
          Join to add your own stories and collaborate.
        </p>
        
        <button
          onClick={handleJoinClick}
          disabled={claiming}
          className="w-full py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
          data-testid="invite-join-btn"
        >
          {claiming ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <UserPlus className="w-5 h-5" />
              <span>{isLoggedIn ? 'Join Folder' : 'View Folder'}</span>
            </>
          )}
        </button>
        
        {!isLoggedIn && (
          <p className="text-xs text-muted-foreground mt-3">
            Already have an account?{' '}
            <button
              onClick={() => {
                localStorage.setItem('pending_invite_link', linkId);
                navigate('/signup');
              }}
              className="text-primary hover:underline"
              data-testid="invite-signin-link"
            >
              Sign in
            </button>
          </p>
        )}
      </motion.div>
    </div>
  );
}

export default InvitePage;
